import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BackButtonProps } from '@/common/components/builder/organisms/SubPage/SubPage.types';
import {
  BookAppointmentValues,
  DeclineReferralValues,
  PatientProfileResponse,
  ReferralStatuses,
  RegisterContactAttemptValues,
} from '../../types';
import { EntryForm } from '../../components/shared/CustomForm/CustomForm.types';
import { formatDate } from '@/common/utils';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { getAppointmentTime } from '../../utils';
import { HELIX_SUPPORT_EMAIL, PATHS } from '@/common/constants';
import { Modal, NotificationVariants, PageLarge, ProcessLoader, SubPage } from '@/common/components';
import { useAddNotification } from '@/modules/notifications/store';
import { useModal, useTab } from '@/common/components/hooks';
import { useMutationUpdatePatientProfile, useQueryPatientProfile } from '../../hooks';

import BookAppointmentContextProvider from '../../providers/BookAppointmentContext/BookAppointmentContext';
import GeneticScheduler from '../../components/shared/GeneticScheduler/GeneticScheduler';
import PatientProfileOverview from '../../components/PatientProfileOverview/PatientProfileOverview';
import ProfileDetails from '../../components/shared/ProfileDetails/ProfileDetails';
import ProfileHistory from '../../components/shared/ProfileHistory/ProfileHistory';
import RescheduleAppointmentModal from '../../components/RescheduleAppointmentModal/RescheduleAppointmentModal';
import useMutationBookAppointment from '../../hooks/useMutationBookAppointment';
import useMutationDeclineReferral from '../../hooks/useMutationDeclineReferral';
import useMutationRegisterContactAttempt from '../../hooks/useMutationRegisterContactAttempt';
import useQueryPatientHistory from '../../hooks/useQueryPatientHistory';

const PatientProfileScreen: FC = () => {
  const navigate = useNavigate();
  const addNotification = useAddNotification();

  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { id } = useParams();
  const { selectedTab } = useTab();
  const [isOnEditionMode, setIsOnEditionMode] = useState(false);
  const { isShowing, toggle } = useModal();

  const successUpdate = (): void => {
    void refetch().then(() => {
      addNotification({
        title: '',
        type: NotificationVariants.SUCCESS,
        autoClose: true,
        description: t('update.success_description'),
      });
    });
  };

  const errorUpdate = (): void => {
    addNotification({
      title: t('update.error_title'),
      type: NotificationVariants.ERROR,
      autoClose: false,
      description: t('update.error_description', { email: HELIX_SUPPORT_EMAIL }),
    });
  };

  const { mutate: updatePatientProfile, isLoading: isLoadingUpdate } = useMutationUpdatePatientProfile(
    successUpdate,
    errorUpdate,
  );

  const { data, isFetching, isLoading, isRefetching, refetch } = useQueryPatientProfile({
    id,
    onError: handleOnError,
    onSuccess: handleOnSuccess,
  });

  const {
    data: historyData,
    isFetching: isHistoryFetching,
    isLoading: isHistoryLoading,
    refetch: patientHistoryRefetch,
  } = useQueryPatientHistory(
    {
      id,
    },
    {
      enabled: false,
    },
  );

  const {
    mutateAsync,
    isError,
    isLoading: isBookingAppointment,
    isSuccess: isAppointmentBooked,
    reset,
  } = useMutationBookAppointment(onBookAppointmentSuccess);

  const { mutate: declineReferral, isLoading: isDecliningAppointment } = useMutationDeclineReferral(
    declineReferralSuccess,
    declineReferralError,
  );

  const {
    mutate: registerContactAttempt,
    isLoading: isRegisteringContactAttempt,
    isSuccess: isContactAttemptSuccess,
  } = useMutationRegisterContactAttempt(registerContactAttemptSuccess, registerContactAttemptError);

  function declineReferralSuccess(): void {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
    });
    void refetch().then(() => {
      addNotification({
        title: t('scheduler.decline_notification.success_title'),
        description: t('scheduler.decline_notification.success_description'),
        type: NotificationVariants.INFO,
        autoClose: false,
      });
    });
  }

  function declineReferralError(): void {
    addNotification({
      title: t('scheduler.decline_notification.error_title'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });
  }

  function registerContactAttemptSuccess(): void {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
    });
    void refetch();
  }

  function registerContactAttemptError(): void {
    addNotification({
      title: t('scheduler.register_contact_attempt_notification.error_title'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });
  }

  function handleOnError(): void {
    addNotification({
      title: t('patient_profile.error'),
      description: t('patient_profile.error_patient_id'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });

    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${selectedTab}`);
  }

  function handleOnSuccess(): void {
    void patientHistoryRefetch();
  }

  function onBookAppointmentSuccess(): void {
    if (isShowing) toggle();
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
    });
    void refetch().then(() => {
      addNotification({
        title: isShowing
          ? t('scheduler.reschedule_appointment_success_notification.title')
          : t('scheduler.book_appointment_success_notification.title'),
        description: isShowing ? undefined : t('scheduler.book_appointment_success_notification.description'),
        type: NotificationVariants.SUCCESS,
        autoClose: true,
      });
    });
  }

  const getBackButton = (): BackButtonProps => {
    return {
      label:
        selectedTab === ''
          ? t('patient_profile.back')
          : t('patient_profile.back_to', { page: t(`${selectedTab}.tab`) }),
      url: `/${PATHS.GENETIC_COUNSELING.ROOT}/${selectedTab}`,
    };
  };

  const handleSubmit = (data: EntryForm[]): void => {
    updatePatientProfile({ id, params: { email: data[0].target, phone: data[1].target, event: 'Update' } });
  };

  const handleBookAppointmentButton = (data: BookAppointmentValues): void => {
    void mutateAsync({ id, values: data });
  };

  const handleRescheduleAppointmentButton = (): void => {
    toggle();
  };

  const handleCancelReschedule = (): void => {
    if (isShowing) toggle();
  };

  const handleDeclineReferral = (data: DeclineReferralValues): void => {
    declineReferral({ id, values: data });
  };

  const handleRegisterContactAttempt = (data: RegisterContactAttemptValues): void => {
    registerContactAttempt({ id, values: data });
  };

  const getTime = (data: PatientProfileResponse): string => {
    const startTime = data?.appointment?.appointmentStartTime;
    const endTime = data?.appointment?.appointmentEndTime;
    const timezone = data?.appointment?.timeZone;

    return getAppointmentTime(startTime, endTime, timezone) ?? '-';
  };

  const getAppointmentDate = (data?: PatientProfileResponse): string => {
    const startTime = data?.appointment?.appointmentStartTime;
    return startTime ? formatDate(startTime) : '-';
  };

  useEffect(() => {
    if (data?.status === ReferralStatuses.PENDING) {
      reset();
    }
  }, [data]);

  return (
    <PageLarge>
      <BookAppointmentContextProvider
        value={{
          isError,
          isLoading: isBookingAppointment,
          isSuccess: isAppointmentBooked,
          handleBookAppointment: handleBookAppointmentButton,
          handleRescheduleAppointment: handleRescheduleAppointmentButton,
          handleCancelReschedule,
        }}
      >
        <SubPage backButton={getBackButton()}>
          <PatientProfileOverview
            data={data}
            isLoading={isLoading || isLoadingUpdate || isRefetching}
            isFetching={isFetching}
          />
          <ProfileDetails
            data={data}
            handleEditionMode={(value) => setIsOnEditionMode(value)}
            isFetching={isFetching}
            isLoading={isLoading || isLoadingUpdate || isRefetching}
            onSubmit={handleSubmit}
          />
        </SubPage>
        <SubPage>
          {data?.status === ReferralStatuses.PENDING && !isAppointmentBooked && (
            <GeneticScheduler
              handleContactAttempt={handleRegisterContactAttempt}
              handleDeclineReferral={handleDeclineReferral}
              isContactAttemptSuccess={isContactAttemptSuccess}
              isOnEditionMode={isOnEditionMode}
            />
          )}
        </SubPage>
        {isShowing && data !== undefined && (
          <RescheduleAppointmentModal
            appointmentData={{
              date: getAppointmentDate(data),
              time: getTime(data),
              type: data?.appointment?.appointmentType ?? '-',
              notes: undefined,
            }}
            show={isShowing}
            onClose={toggle}
          />
        )}
        <SubPage>
          <ProfileHistory data={historyData} isLoading={isHistoryLoading || isHistoryFetching} />
        </SubPage>
      </BookAppointmentContextProvider>
      {isBookingAppointment && (
        <Modal css={{ padding: '80px 0' }} isLocked show={isBookingAppointment}>
          <ProcessLoader data-testid="process-loader-book-appointment" title={t('scheduler.booking_appointment')} />
        </Modal>
      )}
      {isDecliningAppointment && (
        <Modal css={{ padding: '80px 0' }} isLocked show={isDecliningAppointment}>
          <ProcessLoader
            data-testid="process-loader-declining-appointment"
            description={t('scheduler.closing_profile_modal.description')}
            title={t('scheduler.closing_profile_modal.title')}
          />
        </Modal>
      )}
      {isRegisteringContactAttempt && (
        <Modal css={{ padding: '80px 0' }} isLocked show={isRegisteringContactAttempt}>
          <ProcessLoader
            data-testid="process-loader-registering-contact-attempt"
            description={t('scheduler.registering_contact_attempt_modal.description')}
            title={t('scheduler.registering_contact_attempt_modal.title')}
          />
        </Modal>
      )}
    </PageLarge>
  );
};

export default PatientProfileScreen;
